import { useCallback, useEffect, useState } from 'react';
import * as Yup from 'yup';
import cooperplaceApi from 'services/apis/cooperplace';

export function getCteQuestions(emittedId) {
  return cooperplaceApi.get(`loads-emitted/${emittedId}/questions`);
}

export function uploadTempFile(file) {
  const formData = new FormData();
  formData.append('file', file);
  return cooperplaceApi.post('temp-files', formData);
}

export function answerCteQuestionnaire(emittedId, questionnaireAnswers) {
  return cooperplaceApi.post(`loads-emitted/${emittedId}/questions/answers`, {
    questionnaireAnswers,
  });
}

export function isQuestionnaireRequiredError(error) {
  return (
    error?.response?.status === 422 &&
    error?.response?.data?.code === 'QUESTIONNAIRE_REQUIRED'
  );
}

function toAnswerValue(question) {
  if (question?.type === 'CHECKBOX') {
    return (question?.choices || [])
      .filter(choice => choice.selected)
      .map(choice => String(choice.id));
  }
  if (question?.type === 'RADIO') {
    const selectedChoice = (question?.choices || []).find(
      choice => choice.selected
    );
    return selectedChoice ? String(selectedChoice.id) : '';
  }
  return question?.answer === null || question?.answer === undefined
    ? ''
    : String(question.answer);
}

// `questions` is a tree (each question carries its own `subquestions`, recursively)
// since the GET response stopped being a flat list. Flatten it to answer
// "what are the values for every question anywhere in the tree" — subquestions
// keep whatever answer they already had even while hidden.
function flattenTree(questions = []) {
  return questions.reduce((acc, question) => {
    acc.push(question);
    if (question.subquestions?.length) {
      acc.push(...flattenTree(question.subquestions));
    }
    return acc;
  }, []);
}

export function getInitialAnswers(questions = []) {
  return flattenTree(questions).reduce(
    (acc, question) => ({ ...acc, [question.id]: toAnswerValue(question) }),
    {}
  );
}

export function isAnswerSatisfied(question, answer) {
  // PHOTO questions always come with required: false — answering one is
  // optional from the gate's point of view, so it never blocks "Salvar".
  if (question?.type === 'PHOTO') {
    return true;
  }
  if (question?.type === 'CHECKBOX') {
    return Array.isArray(answer) && answer.length > 0;
  }
  return typeof answer === 'string' && answer.trim().length > 0;
}

// Choice ids selected in `question` right now — always compared against that
// same question's own `choices`, never against a different question's ids
// (the backend doc warns question ids and choice ids are separate sequences
// that can coincide).
export function getSelectedChoiceIds(question, answer) {
  const selected = new Set();
  if (question?.type === 'CHECKBOX' && Array.isArray(answer)) {
    answer.forEach(choiceId => selected.add(String(choiceId)));
  } else if (question?.type === 'RADIO' && answer) {
    selected.add(String(answer));
  }
  return selected;
}

// Flat, depth-first list of every question currently applicable: every root
// question, plus — recursively — each subquestion whose `triggeredByChoiceIds`
// matches a choice selected on its own parent. A subquestion whose parent
// isn't included never gets visited, so a hidden branch's descendants are
// hidden too without any extra bookkeeping.
export function getVisibleQuestions(questions = [], answersByQuestionId = {}) {
  return questions.reduce((acc, question) => {
    acc.push(question);
    const selectedChoiceIds = getSelectedChoiceIds(
      question,
      answersByQuestionId[question.id]
    );
    const visibleSubquestions = (question.subquestions || []).filter(sub =>
      (sub.triggeredByChoiceIds || []).some(choiceId =>
        selectedChoiceIds.has(String(choiceId))
      )
    );
    acc.push(...getVisibleQuestions(visibleSubquestions, answersByQuestionId));
    return acc;
  }, []);
}

function computeHasPendingQuestions(questions) {
  const answers = getInitialAnswers(questions);
  const visible = getVisibleQuestions(questions, answers);
  return visible.some(
    question => !isAnswerSatisfied(question, answers[question.id])
  );
}

export function buildQuestionnaireAnswersPayload(
  questions = [],
  answersByQuestionId = {}
) {
  const visible = getVisibleQuestions(questions, answersByQuestionId);
  // PHOTO answers are the hash from uploadTempFile (set as the field's value once
  // uploaded); questions never freshly answered simply stay '' and are dropped by
  // the trailing filter below, same as any other unanswered optional field.
  return visible
    .map(question => {
      const rawAnswer = answersByQuestionId[question.id];
      const answer =
        question.type === 'CHECKBOX'
          ? Array.isArray(rawAnswer)
            ? rawAnswer.join(',')
            : ''
          : String(rawAnswer || '').trim();
      return { cteQuestionId: question.id, answer };
    })
    .filter(entry => entry.answer.length > 0);
}

export function buildQuestionnaireSchema(visibleQuestions = []) {
  // PHOTO is always optional (required: false) — no Yup rule needed for it.
  const shape = visibleQuestions
    .filter(question => question.type !== 'PHOTO')
    .reduce(
      (acc, question) => ({
        ...acc,
        [question.id]:
          question.type === 'CHECKBOX'
            ? Yup.array().min(1, 'Selecione ao menos uma opção')
            : Yup.string().trim().required('Campo obrigatório'),
      }),
      {}
    );
  return Yup.object().shape(shape);
}

export function useCteQuestionnaire(emittedId, { enabled = true } = {}) {
  const [questions, setQuestions] = useState([]);
  const [completed, setCompleted] = useState(false);
  const [answeredAt, setAnsweredAt] = useState(null);
  const [answeredUser, setAnsweredUser] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchQuestions = useCallback(async () => {
    if (!emittedId) return [];
    try {
      setLoading(true);
      setError(null);
      const { data } = await getCteQuestions(emittedId);
      const message = data?.message || {};
      const fetchedQuestions = message.questions || [];
      setQuestions(fetchedQuestions);
      setCompleted(!!message.completed);
      setAnsweredAt(message.answeredAt || null);
      setAnsweredUser(message.answeredUser || null);
      return fetchedQuestions;
    } catch (err) {
      setError(err);
      return [];
    } finally {
      setLoading(false);
    }
  }, [emittedId]);

  useEffect(() => {
    if (enabled && emittedId) {
      fetchQuestions();
    } else {
      setQuestions([]);
      setCompleted(false);
      setAnsweredAt(null);
      setAnsweredUser(null);
      setError(null);
    }
  }, [enabled, emittedId, fetchQuestions]);

  return {
    questions,
    completed,
    answeredAt,
    answeredUser,
    hasPendingQuestions: computeHasPendingQuestions(questions),
    loading,
    error,
    refetch: fetchQuestions,
  };
}
